import type { ApiClient } from "../../../app/api/client";
import type { DocumentLayoutBlock } from "../documentAppearance/documentAppearanceApi";

/** Muss zu SystemDocumentKind im Backend passen. */
export const SYSTEM_DOCUMENT_KIND = {
  cancellationConfirmation: 0,
  restPeriodConfirmation: 1,
  paymentReminder: 2,
  debtCollectionHandover: 3,
  freeMessage: 4,
} as const;

/** Muss zu SystemDocumentField im Backend passen — die Werte stehen im HTML als data-mq-field. */
export const SYSTEM_DOCUMENT_FIELDS = ["salutation", "introduction", "notice", "closing"] as const;
export type SystemDocumentFieldKey = (typeof SYSTEM_DOCUMENT_FIELDS)[number];

/** Serverseitig erzeugte Texte: formatierbar, aber nicht inhaltlich überschreibbar. */
export const SYSTEM_DOCUMENT_GENERATED_STYLE_TARGETS = ["address", "subject"] as const;
export type SystemDocumentGeneratedStyleTargetKey = (typeof SYSTEM_DOCUMENT_GENERATED_STYLE_TARGETS)[number];
export type SystemDocumentTextTargetKey = SystemDocumentFieldKey | SystemDocumentGeneratedStyleTargetKey;

/** Muss zu DocumentTextAlignment im Backend passen. */
export const SYSTEM_DOCUMENT_TEXT_ALIGNMENT = { left: 0, center: 1, right: 2 } as const;

/** Muss zu DocumentAppearanceChannels im Backend passen. */
export const SYSTEM_DOCUMENT_CHANNELS = ["Letter", "Email"] as const;
export type SystemDocumentChannel = (typeof SYSTEM_DOCUMENT_CHANNELS)[number];

export type SystemDocumentTextDto = {
  id?: string | null;
  /** Optionale Kategorie; leer bedeutet allgemeiner Text mit allen Variablen. */
  categoryId?: string | null;
  variantName?: string | null;
  studioId?: string | null;
  subject?: string | null;
  salutation?: string | null;
  introduction?: string | null;
  notice?: string | null;
  closing?: string | null;
  dataBlockSettings?: SystemDocumentDataBlockSettings | null;
  channel: SystemDocumentChannel[];
  /** Gewähltes Erscheinungsbild; leer = das Standardformat des Kanals. */
  documentAppearanceId?: string | null;
  /**
   * Ob Brief und E-Mail verschiedene Texte tragen. Aus: ein Text für beide. An: die Mail wird zum
   * Anschreiben, der Brief zum eigentlichen Schreiben im Anhang.
   */
  splitChannelContent?: boolean;
  /** Die Abweichungen der Mail; leere Felder bedeuten „wie der Brief". */
  emailSubject?: string | null;
  emailSalutation?: string | null;
  emailIntroduction?: string | null;
  emailNotice?: string | null;
  emailClosing?: string | null;
  /**
   * Was dieser Textsatz an den Bausteinen seines Erscheinungsbilds ändert; leer = er folgt der
   * Vorlage vollständig. Weggelassen heißt beim Speichern „unverändert lassen" — ein Client, der
   * das Feld nicht kennt, löscht damit nichts.
   */
  blockOverrides?: DocumentBlockOverrides | null;
  /**
   * Die eigene Fassung der E-Mail bei getrennten Inhalten. Ohne getrennte Inhalte gibt es sie nicht:
   * Dann zeigen beide Ausgaben denselben Satz, und wer im E-Mail-Blatt schreibt, schreibt ihn.
   */
  emailBlockOverrides?: DocumentBlockOverrides | null;
};

/**
 * Die Abweichungen eines Textsatzes von seiner Vorlage. Muss zu `DocumentBlockOverrides` im Backend
 * passen; zusammengeführt wird ausschließlich dort.
 */
export type DocumentBlockOverrides = {
  /** Feldweise Abweichungen je Bausteinschlüssel. */
  blocks: Record<string, Partial<DocumentLayoutBlock>>;
  /**
   * Bausteine der Vorlage, die dieser Textsatz nicht zeigt. Mehr als Inhalt und Sichtbarkeit ändert
   * er nicht — Aufbau und Gestaltung gehören der Vorlage; wer mehr braucht, dupliziert das Format.
   */
  hidden: string[];
};

export type SystemDocumentTextStyle = {
  bold?: boolean | null;
  italic?: boolean | null;
  underline?: boolean | null;
  color?: string | null;
  backgroundColor?: string | null;
  alignment?: number | null;
};

export type SystemDocumentDataBlockPresentation = {
  labels: Record<string, string>;
  labelStyle?: SystemDocumentTextStyle | null;
  valueStyle?: SystemDocumentTextStyle | null;
  headerStyle?: SystemDocumentTextStyle | null;
  cellStyle?: SystemDocumentTextStyle | null;
  totalStyle?: SystemDocumentTextStyle | null;
};

/** Muss den JSON-Eigenschaftsnamen entsprechen, die SystemDocumentHtmlWriter als data-mq-data-role ausgibt. */
export const SYSTEM_DOCUMENT_DATA_STYLE_ROLES = ["labelStyle", "valueStyle", "headerStyle", "cellStyle", "totalStyle"] as const;
export type SystemDocumentDataStyleRole = (typeof SYSTEM_DOCUMENT_DATA_STYLE_ROLES)[number];

export type SystemDocumentDataTarget = {
  blockName: string;
  role: SystemDocumentDataStyleRole;
  /** Nur bei einer bearbeitbaren Bezeichnung gesetzt; die Formatrolle gilt trotzdem für ihren Typ. */
  labelKey?: string | null;
};

export type SystemDocumentDataBlockSettings = {
  blocks: Record<string, SystemDocumentDataBlockPresentation>;
  /** Feldbezogene Überschreibungen; leer lässt die Vorgabe aus dem Erscheinungsbild wirken. */
  fieldStyles?: Partial<Record<SystemDocumentTextTargetKey, SystemDocumentTextStyle>>;
};

export type SystemDocumentDataBlockDescriptor = {
  name: string;
  kind: number;
  labels: Array<{ key: string; label: string }>;
};

export type EffectiveSystemDocumentTextDto = {
  subject: string;
  salutation: string;
  introduction: string;
  notice: string;
  closing: string;
  dataBlockSettings: SystemDocumentDataBlockSettings;
  splitChannelContent?: boolean;
  emailSubject?: string | null;
  emailSalutation?: string | null;
  emailIntroduction?: string | null;
  emailNotice?: string | null;
  emailClosing?: string | null;
};

/** Ein Baustein des Dokuments, so weit die Bearbeitungsfläche ihn kennen muss. */
export type SystemDocumentLayoutBlockDto = {
  /** Der zusammengeführte Baustein — Vorlage plus das, was dieser Textsatz daran ändert. */
  block: DocumentLayoutBlock;
  movable: boolean;
};

export type SystemDocumentPreviewDto = {
  subject: string;
  contentHtml: string;
  emailHtml: string;
  plainText: string;
  dataBlocks: SystemDocumentDataBlockDescriptor[];
  /** Die Bausteine des Dokuments — Grundlage für Griff, Verschieben und Auswahl. */
  blocks: SystemDocumentLayoutBlockDto[];
  /** Die Bausteine der E-Mail; getrennt, weil das Erscheinungsbild je Kanal abweichen darf. */
  emailBlocks?: SystemDocumentLayoutBlockDto[] | null;
  /** Musterwerte für die token-sichere Bearbeitung in der Vorschau. */
  templateValues?: Record<string, string> | null;
  /**
   * Ob Brief und E-Mail verschiedene Texte tragen. Wer die Ansicht bearbeitet, muss wissen, in
   * welchen der beiden Sätze er schreibt — sonst hinge eine Änderung auf dem E-Mail-Blatt am
   * Brieftext und ginge ins Leere.
   */
  hasSeparateEmailContent?: boolean;
};

export type SystemDocumentPreviewRequest = {
  studioId?: string | null;
  /**
   * Der echte Vorgang, über den das Dokument entsteht. Ohne ihn baut der Server aus Beispieldaten
   * — das ist der Fall der Textpflege. Der Versand setzt ihn, damit vor dem Absenden dieselbe
   * Fassung auf dem Blatt steht, die der Empfänger bekommt.
   */
  referenceId?: string | null;
  variantId?: string | null;
  /** Noch nicht gespeicherte Auswahl des Erscheinungsbilds. */
  documentAppearanceId?: string | null;
  /** Ausgabesprache der ausgelieferten Vorgaben und der Datums-/Betragsschreibweise. */
  language: string;
  subject?: string | null;
  salutation?: string | null;
  introduction?: string | null;
  notice?: string | null;
  closing?: string | null;
  dataBlockSettings?: SystemDocumentDataBlockSettings | null;
  /** false liefert die Variablen unverändert für die Bearbeitung im Dokument. */
  resolveTemplateVariables?: boolean;
  /** Ungespeicherte Kanalauswahl des Editors. */
  channel?: SystemDocumentChannel[] | null;
  /**
   * Die noch nicht gespeicherten Abweichungen von den Bausteinen des Erscheinungsbilds. Ohne sie
   * zeigte die Vorschau die Vorlage und nicht das, was der Textsatz daraus macht.
   */
  blockOverrides?: DocumentBlockOverrides | null;
  emailBlockOverrides?: DocumentBlockOverrides | null;
  /**
   * Der abweichende Wortlaut der E-Mail. Er wirkt nur, wenn der Textsatz getrennte Inhalte fuehrt;
   * sonst gilt fuer beide Ausgaben derselbe Text aus den Feldern oben.
   */
  email?: SystemDocumentEmailOverrides | null;
};

/** Die Abweichung der E-Mail vom Brief; ein leeres Feld heisst "wie der Brief". */
export type SystemDocumentEmailOverrides = {
  subject?: string | null;
  salutation?: string | null;
  introduction?: string | null;
  notice?: string | null;
  closing?: string | null;
};

export type SystemDocumentBlockVariableDto = {
  name: string;
  /** Optional, damit ein Frontend während eines gestaffelten Deployments auch ältere Antworten liest. */
  labelDe?: string;
  labelEn?: string;
  groupDe?: string;
  groupEn?: string;
};

export type SystemDocumentKindDto = {
  value: number;
  kind: string;
  blockVariables: SystemDocumentBlockVariableDto[];
  /** Optional für gestaffelte Deployments; blockVariables bleibt die schmale Rückfallebene. */
  tokens?: SystemDocumentTemplateTokenDto[];
};

export type TextTemplateCategoryDto = {
  id: string;
  name: string;
  isActive: boolean;
  isCancellationLetter: boolean;
  isRestPeriodLetter: boolean;
  isReminderLetter: boolean;
  isDebtCollectionLetter: boolean;
  isContractConfirmation: boolean;
  isNotification: boolean;
  /** Ausschließlich der Filter für die Variablen-Combobox des Editors. */
  variableScope: string;
  allowedStudioIds?: string[] | null;
};

export type SystemDocumentTemplateTokenDto = {
  key: string;
  groupDe: string;
  labelDe: string;
  groupEn: string;
  labelEn: string;
  kind: "value" | "block";
};

export function systemDocumentKindLabel(kind: number) {
  switch (kind) {
    case SYSTEM_DOCUMENT_KIND.restPeriodConfirmation: return "Rest period confirmation";
    case SYSTEM_DOCUMENT_KIND.paymentReminder: return "Payment reminder";
    case SYSTEM_DOCUMENT_KIND.debtCollectionHandover: return "Debt collection handover";
    case SYSTEM_DOCUMENT_KIND.freeMessage: return "Free message";
    default: return "Cancellation confirmation";
  }
}

export function listSystemDocumentTexts(
  api: ApiClient,
  query?: { purpose?: string; documentKind?: number },
  withStudioScope: (url: string) => string = (url) => url
) {
  const params = new URLSearchParams();
  if (query?.purpose) params.set("purpose", query.purpose);
  if (query?.documentKind != null) params.set("documentKind", String(query.documentKind));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return api.get<SystemDocumentTextDto[]>(withStudioScope(`/api/v1/system-document-texts${suffix}`));
}

export function loadEffectiveTexts(
  api: ApiClient,
  variantId: string | null | undefined,
  studioId: string | null | undefined,
  language: string
) {
  const params = new URLSearchParams({ language });
  if (variantId) params.set("variantId", variantId);
  if (studioId) params.set("studioId", studioId);
  return api.get<EffectiveSystemDocumentTextDto>(`/api/v1/system-document-texts/effective?${params.toString()}`);
}

/** Was beim Speichern gestellt wird; die Kategorie bleibt optionales Filtermerkmal. */
export type SystemDocumentTextSaveDto = SystemDocumentTextDto;

export function saveSystemDocumentText(api: ApiClient, dto: SystemDocumentTextSaveDto) {
  return api.put<SystemDocumentTextDto>("/api/v1/system-document-texts", dto);
}

export function deleteSystemDocumentText(api: ApiClient, id: string) {
  return api.del(`/api/v1/system-document-texts/${id}`);
}

export function listSystemDocumentKinds(api: ApiClient) {
  return api.get<SystemDocumentKindDto[]>("/api/v1/system-documents/kinds");
}

export function listSystemDocumentTokens(api: ApiClient, scope?: string | null) {
  const suffix = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return api.get<SystemDocumentTemplateTokenDto[]>(`/api/v1/system-documents/tokens${suffix}`);
}

export function listTextTemplateCategories(api: ApiClient) {
  return api.get<TextTemplateCategoryDto[]>("/api/v1/text-template-categories");
}

export function createTextTemplateCategory(
  api: ApiClient,
  dto: Omit<TextTemplateCategoryDto, "id" | "variableScope">
) {
  return api.post<TextTemplateCategoryDto>("/api/v1/text-template-categories", dto);
}

/** Legt bei Bedarf die Kategorie mit Flag Benachrichtigungen an und gibt sie zurück. */
export async function ensureNotificationTextCategory(api: ApiClient, name: string) {
  const categories = await listTextTemplateCategories(api);
  const existing = (categories ?? []).find((entry) => entry.isActive && entry.isNotification);
  if (existing) return existing;
  try {
    return await createTextTemplateCategory(api, {
      name,
      isActive: true,
      isCancellationLetter: false,
      isRestPeriodLetter: false,
      isReminderLetter: false,
      isDebtCollectionLetter: false,
      isContractConfirmation: false,
      isNotification: true,
    });
  } catch {
    const retry = await listTextTemplateCategories(api);
    return (retry ?? []).find((entry) => entry.isActive && entry.isNotification) ?? null;
  }
}

export function previewSystemDocument(api: ApiClient, kind: string, request: SystemDocumentPreviewRequest) {
  return api.post<SystemDocumentPreviewDto>(
    `/api/v1/system-documents/${kind}/preview`,
    request
  );
}

/**
 * Was ein Einzelversand abweichend stellen darf. Alles davon gilt für genau diese Sendung; in den
 * gepflegten Textsatz wird nichts zurückgeschrieben. Leere Felder bedeuten „wie gepflegt".
 */
export type SystemDocumentSendRequest = {
  referenceId: string;
  sendEmail: boolean;
  variantId?: string | null;
  language?: string;
  subject?: string | null;
  salutation?: string | null;
  introduction?: string | null;
  notice?: string | null;
  closing?: string | null;
  /** Der abweichende Wortlaut der E-Mail bei getrennten Inhalten. */
  email?: SystemDocumentEmailOverrides | null;
  /** Der Titel in der digitalen Akte; ohne Angabe gilt der Betreff. */
  documentTitle?: string | null;
  /** Ein abweichendes Erscheinungsbild — nur mit dem Recht `templates.appearanceOnSend`. */
  documentAppearanceId?: string | null;
  /** Der im Blatt geschriebene Wortlaut je Baustein. */
  blockOverrides?: DocumentBlockOverrides | null;
  /** Die eigene Fassung der E-Mail bei getrennten Inhalten. */
  emailBlockOverrides?: DocumentBlockOverrides | null;
};

export type SystemDocumentSendResponse = {
  documentId?: string | null;
  emailDocumentId?: string | null;
  outboundMessageId?: string | null;
  warnings: string[];
};

/** Erzeugt das Dokument zum Vorgang und übergibt es der bestehenden Versandkette. */
export function sendSystemDocument(api: ApiClient, kind: string, request: SystemDocumentSendRequest) {
  return api.post<SystemDocumentSendResponse>(
    `/api/v1/system-documents/${encodeURIComponent(kind)}/send`,
    request
  );
}

/** Das echte Brief-PDF aus den gerade bearbeiteten Texten. */
export function previewSystemDocumentPdf(api: ApiClient, kind: string, request: SystemDocumentPreviewRequest) {
  return api.blob(`/api/v1/system-documents/${encodeURIComponent(kind)}/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
}
